import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, AlertTriangle, CheckCircle, Scale, Clock, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-of-service-page">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
            <Scale className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">Terms of Service</h1>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          {/* Important Notice */}
          <section className="bg-amber-50 border-2 border-amber-200 rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-amber-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Important Financial Disclaimer
            </h2>
            <div className="space-y-3 text-amber-800">
              <p>
                <strong>My Budget Tracker is a personal finance management tool, NOT a financial advisor.</strong>
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>This service provides tools to track and organize your financial data</li>
                <li>We do NOT provide investment advice, tax advice, or financial planning services</li>
                <li>Any insights or recommendations generated are for informational purposes only</li>
                <li>Always consult qualified financial professionals for important financial decisions</li>
                <li>Past spending patterns do not guarantee future financial outcomes</li>
              </ul>
            </div>
          </section>

          {/* Agreement */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Agreement to Terms
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              By accessing or using My Budget Tracker ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you do not have permission to access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          {/* Eligibility */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Eligibility</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>You must be at least 18 years old to use this Service</li>
              <li>You must provide accurate and complete registration information</li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You must notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          {/* Service Description */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Service Description
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>My Budget Tracker provides the following services:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Transaction Tracking:</strong> Record and categorize income and expenses</li>
                <li><strong>Budget Management:</strong> Set and monitor spending limits by category</li>
                <li><strong>Data Import:</strong> Import transactions from CSV and Excel files</li>
                <li><strong>Financial Insights:</strong> View charts and analytics of your spending patterns</li>
                <li><strong>AI Categorization:</strong> Automatic categorization of imported transactions</li>
                <li><strong>Data Export:</strong> Download your financial data in various formats</li>
              </ul>
            </div>
          </section>

          {/* Subscription & Payments */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Subscription and Payments
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="font-medium text-foreground mb-2">Free Tier</h3>
                <p>Basic features available at no cost with usage limitations</p>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Premium Subscriptions</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Billed monthly or annually as selected during checkout</li>
                  <li>Payments processed securely via Stripe</li>
                  <li>Subscriptions auto-renew unless cancelled before renewal date</li>
                  <li>No refunds for partial subscription periods</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Price Changes</h3>
                <p>We reserve the right to modify subscription prices with 30 days notice. Current subscribers will be notified before changes affect their next billing cycle.</p>
              </div>
              <div className="bg-green-50 border border-green-200 p-4 rounded-sm">
                <h3 className="font-medium text-green-900 mb-2">Promotional Period</h3>
                <p className="text-green-800">Until March 25, 2026, all premium features are available free of charge. No payment required during this promotional period.</p>
              </div>
            </div>
          </section>

          {/* User Responsibilities */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">User Responsibilities</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>You are responsible for the accuracy of data you enter or import</li>
              <li>You must keep your login credentials secure and confidential</li>
              <li>You agree not to share your account with others</li>
              <li>You are responsible for all activities under your account</li>
              <li>You must comply with all applicable laws and regulations</li>
            </ul>
          </section>

          {/* Prohibited Uses */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Prohibited Uses
            </h2>
            <p className="text-muted-foreground mb-4">You agree NOT to use the Service to:</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Violate any laws or regulations</li>
              <li>Infringe on intellectual property rights</li>
              <li>Upload malicious code, viruses, or harmful content</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service</li>
              <li>Collect user data without authorization</li>
              <li>Use the Service for any fraudulent or illegal purpose</li>
              <li>Resell or commercially exploit the Service without permission</li>
            </ul>
          </section>

          {/* Data Accuracy */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Data Accuracy Disclaimer</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong>We do not guarantee the accuracy of AI-generated categorizations or insights.</strong> The AI categorization feature uses machine learning to suggest categories based on transaction descriptions, but may not always be accurate.
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Always review and verify AI-suggested categories</li>
                <li>Manually correct any miscategorized transactions</li>
                <li>Do not rely solely on automated insights for financial decisions</li>
              </ul>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Limitation of Liability</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, FINANCE TRACKER SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Loss of profits, data, use, or goodwill</li>
                <li>Financial losses resulting from reliance on the Service</li>
                <li>Errors or inaccuracies in transaction categorization</li>
                <li>Service interruptions or downtime</li>
                <li>Unauthorized access to your account due to your failure to secure credentials</li>
              </ul>
              <p className="mt-4">
                Our total liability shall not exceed the amount you paid us in the 12 months preceding any claim.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Intellectual Property</h2>
            <p className="text-muted-foreground">
              The Service, including its original content, features, and functionality, is owned by My Budget Tracker Pty Ltd and is protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
            </p>
          </section>

          {/* Termination */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Termination</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>We may terminate or suspend your account immediately, without prior notice, for:</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Breach of these Terms</li>
                <li>Fraudulent or illegal activity</li>
                <li>Conduct harmful to other users or the Service</li>
              </ul>
              <p className="mt-4">
                You may terminate your account at any time through the account settings. Upon termination, your right to use the Service ceases immediately.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Governing Law</h2>
            <p className="text-muted-foreground">
              These Terms shall be governed by and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of New South Wales, Australia.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Changes to Terms</h2>
            <p className="text-muted-foreground">
              We reserve the right to modify these Terms at any time. We will notify users of material changes via email or prominent notice on our website. Continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Contact Us</h2>
            <p className="text-muted-foreground">
              For any questions about these Terms, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-sm text-muted-foreground">
              <p><strong>Email:</strong> legal@financetracker.com</p>
              <p><strong>Address:</strong> My Budget Tracker Pty Ltd, Sydney, NSW, Australia</p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 My Budget Tracker. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
